import json from '@/ts/json';
import { defineStore } from 'pinia';

const wslink = process.env.NODE_ENV == 'development' ? 'ws://localhost:9999/api/admin/ws' : 'wss://team.stima.pro/api/admin/ws'
const wsdebug = process.env.NODE_ENV == 'development'
const noop = () => undefined
const log = (...args) => {
  if (wsdebug) console.log(...args)
}

export const WebsocketStore = defineStore('ws', {
  state: () => ({
    wsconn: undefined,
    online: false,
    connecting: false,
    subscribers : [],
    global : [], //global subscribers
    queue: [],
    maxQueue: 100,
  }),

  getters: {
    conn: (state) =>{

      if (state.wsconn) return state.wsconn

      state.connecting = true
      const wss = new WebSocketSingleton(wslink);

      wss.addEventListener('open', () => {
        log('ws connected');
        state.online = true
        state.connecting = false
        while (state.queue.length && wss.readyState === WebSocket.OPEN) {
          wss.send(state.queue.shift())
        }
      });

      wss.addEventListener('message', (e) => {
        state.online = true
        state.connecting = false
        let res
        try {
          res = json.parse(e.data)
        } catch (error) {
          console.error('ws parse error:', error);
          return
        }
        state.subscribers.slice().forEach(p => {
          if (res.name != p.name) return
          try {
            p.handler(res)
          } catch (error) {
            console.error('ws subscriber error:', error)
          }
        });
        state.global.slice().forEach(handler => {
          try {
            handler(res)
          } catch (error) {
            console.error('ws global handler error:', error)
          }
        });
        log('ws data', res.name);
      });

      wss.addEventListener('close', () => {
        state.online = false
        state.connecting = true
        log('ws disconnected');
      });

      wss.addEventListener('error', (error) => {
        state.online = false
        state.connecting = false
        console.error('ws error:', error);
      });


      state.wsconn = wss
      return state.wsconn
    }
  },

  actions: {

    send(name, req){
      let message = json.stringify({name, req})
      let conn = this.conn
      if (conn.readyState === WebSocket.OPEN) {
        conn.send(message)
        return true
      }
      this.queue.push(message)
      if (this.queue.length > this.maxQueue) this.queue.splice(0, this.queue.length - this.maxQueue)
      return false
    },

    // subscribe name
    subscribe(name, handler){
      if (!name || typeof handler != 'function') return noop
      this.conn
      let exists = this.subscribers.find(p => p.name == name && p.handler === handler)
      if (!exists) this.subscribers.push({name, handler})
      return () => {
        this.subscribers = this.subscribers.filter(p => p.handler !== handler || p.name !== name)
      }
    },
    // subscribe global
    handler(handler){
      if (typeof handler != 'function') return noop
      this.conn
      if (this.global.indexOf(handler) == -1) this.global.push(handler)
      return () => {
        this.global = this.global.filter(p => p !== handler)
      }
    },
    close(){
      if (!this.wsconn) return
      this.wsconn.close()
      this.wsconn = undefined
      this.online = false
      this.connecting = false
      this.queue = []
    },



  },
})


class WebSocketSingleton {
  static instance = null;
  static getInstance(url, protocols = []) {
    if (!WebSocketSingleton.instance) {
      WebSocketSingleton.instance = new WebSocketSingleton(url, protocols);
    }
    return WebSocketSingleton.instance;
  }

  constructor(url, protocols = []) {
    if (WebSocketSingleton.instance) {
      return WebSocketSingleton.instance;
    }

    this.url = url;
    this.protocols = protocols;
    this.socket = null;
    this.reconnectTimer = undefined;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = Infinity;
    this.reconnectInterval = 1000;
    this.maxReconnectInterval = 30000;
    this.forcedClose = false;
    this.eventListeners = {
      open: [],
      close: [],
      error: [],
      message: []
    };

    this.connect();
    WebSocketSingleton.instance = this;
  }

  connect() {
    if (this.socket && [WebSocket.CONNECTING, WebSocket.OPEN].indexOf(this.socket.readyState) > -1) return
    this.socket = new WebSocket(this.url, this.protocols);

    this.socket.onopen = (event) => {
      this.reconnectAttempts = 0;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer)
        this.reconnectTimer = undefined
      }
      this.dispatch('open', event);
      log('ws connected')
    };

    this.socket.onclose = (event) => {
      this.dispatch('close', event);
      if (!this.forcedClose) this.reconnect();
    };

    this.socket.onerror = (event) => {
      this.dispatch('error', event);
    };

    this.socket.onmessage = (event) => {
      this.dispatch('message', event);
    };
  }

  reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      log(`Max reconnection attempts (${this.maxReconnectAttempts}) reached`);
      return;
    }

    this.reconnectAttempts++;
    let timeout = Math.min(this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectInterval)
    log(`ws reconnect attempt ${this.reconnectAttempts} in ${timeout}ms`);

    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined
      if (!this.forcedClose) this.connect();
    }, timeout);
  }

  send(data) {
    if (this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else {
      log('WebSocket is not connected');
    }
  }

  close() {
    this.forcedClose = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer)
    this.socket?.close();
    if (WebSocketSingleton.instance === this) WebSocketSingleton.instance = null
  }

  addEventListener(type, listener) {
    this.eventListeners[type]?.push(listener);
  }

  removeEventListener(type, listener) {
    if (this.eventListeners[type]) {
      this.eventListeners[type] = this.eventListeners[type].filter(
        l => l !== listener
      );
    }
  }

  dispatch(type, event) {
    this.eventListeners[type]?.forEach(listener => listener.call(this, event));
  }

  get readyState() {
    return this.socket?.readyState ?? WebSocket.CLOSED;
  }
}
